import { useInfiniteQuery } from '@zg-rentals/react-query5';

import { RentalManagerAccountViewZrmV2WebApiHandler_list } from '@zg-rentals/ts-rental-manager-api-beta-client';

import type { transformFiltersToApiParams } from '../zram';

import {
  type AccountViewListingGroupDto,
  type AccountViewListingGroupsResponseDto,
  type ListingCountDto,
  type RentalManagerKeyPagerDto,
} from '@zg-rentals/ts-rental-manager-api-client';

// ListParams is not exported from the API client, so it's copied here.
export type ListParams = {
  includeActive?: boolean;
  includeArchived?: boolean;
  includeInactive?: boolean;
  includeUnarchived?: boolean;
  limit?: number;
  searchText?: string;
  startKeyExclusive?: string;
};

// Params accepted by the hook: ListParams or the return type of transformFiltersToApiParams (extra keys are ignored by the API).
export type GroupablePropertiesListParams = ListParams | ReturnType<typeof transformFiltersToApiParams>;

function getListApiParams(params: GroupablePropertiesListParams): Omit<ListParams, 'startKeyExclusive'> {
  const apiParams = {
    includeActive: params.includeActive,
    includeArchived: params.includeArchived,
    includeInactive: params.includeInactive,
    includeUnarchived: params.includeUnarchived,
    limit: params.limit,
    searchText: params.searchText,
  };

  if (!apiParams.searchText) delete apiParams.searchText;
  if (apiParams.includeActive === undefined) delete apiParams.includeActive;
  if (apiParams.includeInactive === undefined) delete apiParams.includeInactive;
  if (apiParams.includeArchived === undefined) delete apiParams.includeArchived;
  if (apiParams.includeUnarchived === undefined) delete apiParams.includeUnarchived;
  if (apiParams.limit === undefined) delete apiParams.limit;

  return apiParams;
}

// Flattened shape returned when pages are loaded; empty object when no data yet
export type GroupablePropertiesData = {
  listingGroups?: Array<AccountViewListingGroupDto>;
  listingCount?: ListingCountDto;
  pager?: RentalManagerKeyPagerDto;
};

export type UseGetPropertiesListOptions = {
  enabled?: boolean;
};

export const useGetPropertiesList = (
  params: GroupablePropertiesListParams,
  options: UseGetPropertiesListOptions = {},
) => {
  const { enabled = true } = options;
  const { includeActive, includeArchived, includeInactive, includeUnarchived, limit, searchText = '' } = params;

  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError, isFetching } =
    useInfiniteQuery<
      AccountViewListingGroupsResponseDto,
      Error,
      { pages: Array<AccountViewListingGroupsResponseDto> },
      Array<string | boolean | number | undefined>,
      string
    >({
      queryKey: [
        'getGroupableProperties',
        searchText,
        includeActive,
        includeInactive,
        includeArchived,
        includeUnarchived,
        limit,
      ],
      queryFn: async ({ pageParam }) => {
        const apiParams = getListApiParams(params);

        return RentalManagerAccountViewZrmV2WebApiHandler_list({
          ...apiParams,
          startKeyExclusive: pageParam,
        });
      },
      initialPageParam: '',
      getNextPageParam: (lastPage) => (lastPage.pager?.hasMore ? lastPage.pager.endKey : undefined),
      enabled,
      // Keep previous data during refetch to prevent UI flicker
      placeholderData: (previousData) => {
        if (!previousData?.pages?.length) {
          return;
        }

        return previousData;
      },
    });

  const propertiesData: GroupablePropertiesData = data?.pages.length
    ? {
        listingGroups: data.pages.flatMap((page) => page.listingGroups),
        listingCount: data.pages[0].listingCount,
        pager: data.pages[data.pages.length - 1].pager,
      }
    : {};

  return {
    data: propertiesData,
    error,
    isPending,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
